/**
 * StepEducation — add 1-2 education entries (simple form, no AI).
 */

import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import type { CVData, Education } from '../../../types';

interface StepEducationProps {
  data: CVData;
  updateEducation: (index: number, field: 'school' | 'degree' | 'date' | 'startDate' | 'endDate', value: string) => void;
  addEducation: () => void;
  removeEducation: (index: number) => void;
}

export function StepEducation({
  data,
  updateEducation,
  addEducation,
  removeEducation,
}: StepEducationProps) {
  const education = data.education;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Ta formation</h1>
        <p className="text-sm text-white/60">
          Diplômes, certifications, formations. Commence par le plus récent.
        </p>
      </div>

      <div className="space-y-4">
        {education.map((edu, i) => (
          <EducationCard
            key={i}
            index={i}
            edu={edu}
            canRemove={education.length > 1}
            onUpdate={updateEducation}
            onRemove={removeEducation}
          />
        ))}
      </div>

      {education.length < 4 && (
        <button
          type="button"
          onClick={addEducation}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-dashed border-white/20 text-white/50 hover:border-white/40 hover:text-white/70 transition-all w-full justify-center"
        >
          <Plus className="size-4" />
          Ajouter une formation
        </button>
      )}
    </div>
  );
}

function EducationCard({
  index,
  edu,
  canRemove,
  onUpdate,
  onRemove,
}: {
  index: number;
  edu: Education;
  canRemove: boolean;
  onUpdate: (i: number, f: 'school' | 'degree' | 'date' | 'startDate' | 'endDate', v: string) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-white/40 uppercase tracking-wider">
          Formation {index + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="size-3.5" />
          </button>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`wiz-edu-deg-${index}`} className="text-xs text-white/70">Diplôme / Formation</Label>
        <Input
          id={`wiz-edu-deg-${index}`}
          type="text"
          value={edu.degree}
          onChange={(e) => onUpdate(index, 'degree', e.target.value)}
          placeholder="Master Informatique"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`wiz-edu-school-${index}`} className="text-xs text-white/70">Établissement</Label>
        <Input
          id={`wiz-edu-school-${index}`}
          type="text"
          value={edu.school}
          onChange={(e) => onUpdate(index, 'school', e.target.value)}
          placeholder="Université Paris-Saclay"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`wiz-edu-date-${index}`} className="text-xs text-white/70">Année / Période</Label>
        <Input
          id={`wiz-edu-date-${index}`}
          type="text"
          value={edu.date}
          onChange={(e) => onUpdate(index, 'date', e.target.value)}
          placeholder="2020 — 2022"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
        />
      </div>
    </div>
  );
}
